import { keepPreviousData, useIsFetching, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSocialCoachSession } from "./useSocialCoachSession";
import { SuperPageServices, type GetSuperPagesParams } from "../services/bedrock/SuperPageServices";
import { CancelRequestOptions } from "@/services/bedrock";

function buildSuperPagesQueryOptions(
  coachId: number | undefined,
  params: GetSuperPagesParams = {},
  options?: CancelRequestOptions
) {
  const categoryKey = Array.isArray(params.categoryId) ? params.categoryId.slice().sort() : params.categoryId ?? null;
  const extraFieldsKey = params.extraFields?.slice().sort() ?? [];
  return {
    queryKey: [
      "coachSuperPagesList",
      coachId,
      params.search,
      params.status,
      categoryKey,
      params.uncategorized,
      extraFieldsKey,
      params.page,
      params.perPage,
      params.sort,
      params.order,
    ] as const,
    queryFn: () => SuperPageServices.getSuperPages(params, options),
    staleTime: 1000 * 60 * 5, // 5 minutes
  };
}

export const useSuperPageSearch = () => {
  const { coachId } = useSocialCoachSession();
  const queryClient = useQueryClient();

  const searchSuperPages = (params: GetSuperPagesParams = {}) => {
    const options = buildSuperPagesQueryOptions(coachId, params);
    const { data: superPages, isFetching } = useQuery({
      ...options,
      placeholderData: keepPreviousData,
    });
    return { superPages, isFetching };
  };

  const fetchSuperPages = (params: GetSuperPagesParams = {}, options?: CancelRequestOptions) =>
    queryClient.fetchQuery(buildSuperPagesQueryOptions(coachId, params, options));

  const clearSuperPagesSearchCache = () => {
    queryClient.invalidateQueries({ queryKey: ["coachSuperPagesList", coachId] }).catch(console.error);
  };

  return {
    searchSuperPages,
    fetchSuperPages,
    clearSuperPagesSearchCache,
  };
};

export const useIsAnySuperPageFetching = () => {
  const { coachId } = useSocialCoachSession();

  const fetchingCount = useIsFetching({
    queryKey: ["coachSuperPagesList", coachId],
  });

  return fetchingCount > 0;
};
